/**
 * BOQEmailPage - Standalone page for composing and sending BOQ emails
 * Opens in a new browser tab with pre-filled BOQ summary table
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Send, Loader2, Mail, ExternalLink } from 'lucide-react';
import { computeBoqTotals, pickRate, pickActualRate, computeConsultancyMilestone, applyMilestoneToBillableGrand } from '@/utils/boqTotals';

export default function BOQEmailPage() {
  const { worksheetId } = useParams();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  
  const [worksheet, setWorksheet] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [toEmail, setToEmail] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [additionalNote, setAdditionalNote] = useState('');
  const [rateView, setRateView] = useState('client');

  const [boqCategories, setBoqCategories] = useState([]);

  useEffect(() => {
    fetchData();
    fetchBoqCategories();
  }, [worksheetId]);

  const fetchBoqCategories = async () => {
    try {
      const resp = await axios.get(`${API}/boq-categories/for-tasks`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setBoqCategories(resp.data || []);
    } catch {}
  };

  const getCategoryLabel = (item) => {
    const mainCat = boqCategories.find(c => (c.main_category_id || c.id) === item.main_category_id);
    if (!mainCat) return '';
    let label = mainCat.group_name;
    if (item.sub_category_id) {
      const subCats = mainCat.categories || mainCat.sub_categories || [];
      const subCat = subCats.find(s => (s.id || s.code) === item.sub_category_id);
      if (subCat) label += ` / ${subCat.name}`;
    }
    return label;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [wsResp, projResp] = await Promise.all([
        axios.get(`${API}/boq-worksheets`, { headers }),
        projectId ? axios.get(`${API}/projects/${projectId}`, { headers }).catch(() => null) : null,
      ]);

      const ws = wsResp.data.find(w => w.id === worksheetId);
      if (ws) {
        setWorksheet(ws);
        // iter-304c: subject = "Project: {name} (PRJ-XXXX) · BOQ: {worksheet_number}"
        // Falls back gracefully when project hasn't loaded yet.
        const projName = projResp?.data?.name || ws.project_name || 'Project';
        const projNum = projResp?.data?.project_number || projResp?.data?.code || '';
        const wsNum = ws.worksheet_number || 'BOQ';
        const subj = projNum
          ? `Project: ${projName} (${projNum}) · BOQ: ${wsNum}`
          : `Project: ${projName} · BOQ: ${wsNum}`;
        setSubject(subj);
      }

      if (projResp?.data) {
        setProject(projResp.data);
        // Pre-fill client email if available
        if (projResp.data.client_email) {
          setToEmail(projResp.data.client_email);
        }
      }
    } catch (error) {
      toast.error('Failed to load BOQ data');
    } finally {
      setLoading(false);
    }
  };

  const getItemRate = (item) => pickRate(item, rateView);

  const getTotal = () => {
    if (!worksheet?.items) return 0;
    return worksheet.items.reduce((sum, item) => {
      if (item.is_discount || item.item_type === 'header') return sum;
      return sum + (item.quantity || 0) * getItemRate(item);
    }, 0);
  };

  // iter-312o: single source of truth — same cascade as Edit BOQ / PDF / Excel.
  const computeTotals = () => computeBoqTotals(worksheet?.items || [], {
    layer: rateView,
    pricingModel: worksheet?.pricing_model,
    discountType: worksheet?.discount_type || 'value',
    // Edit BOQ stores percentage discounts in `discount_value` (with discount_type='percentage');
    // legacy worksheets may use `discount_percentage` separately. Honour both.
    discountValue: (worksheet?.discount_type === 'percentage')
      ? (parseFloat(worksheet?.discount_value || worksheet?.discount_percentage || 0) || 0)
      : (parseFloat(worksheet?.discount_value || 0) || 0),
    freightPct: worksheet?.freight_pct || 0,
    labourHandlingPct: worksheet?.labour_handling_pct || 0,
    consultancyType: worksheet?.consultancy_type || 'percentage',
    consultancyPct: worksheet?.consultancy_pct || 0,
    consultancyValue: worksheet?.consultancy_value || 0,
    // iter-312p / iter-338: Actual-side deductions. SSOT (matches Edit BOQ +
    // Public BOQ + PDF + Excel via `_resolve_boq_deductions`):
    //   1. If `applied_payments` list has rows → sum their `applied_amount`
    //   2. Else if `client_payments_applied_override` is set → use that
    //   3. Else → 0 (no auto-from-ledger fallback)
    // Previously the email fell back to `client_payments_received` (TOTAL
    // received across the project) which double-counted vs the actual applied
    // portion — fix per Msg-680.
    clientPaymentsApplied: (() => {
      const list = worksheet?.applied_payments_detail || worksheet?.applied_payments || [];
      const summed = list.reduce((s, ap) => s + (parseFloat(ap?.applied_amount) || 0), 0);
      if (summed > 0) return summed;
      if (worksheet?.client_payments_applied_override != null) return +worksheet.client_payments_applied_override;
      return 0;
    })(),
    creditNotesApplied: worksheet?.credit_notes_applied_override != null
      ? +worksheet.credit_notes_applied_override
      : 0,
  });

  const generateEmailHTML = () => {
    if (!worksheet) return '';
    const items = worksheet.items?.filter(i => !i.is_discount) || [];
    const viewLabel = rateView === 'vendor' ? 'Vendor' : rateView === 'admin' ? 'Admin' : 'Client';
    const boqLink = worksheet.public_link || worksheet.temp_link || '';

    // iter-312o: derive ALL summary numbers from the shared helper so the email
    // body matches Edit BOQ / PDF / Excel exactly. No bespoke cascade here.
    const t = computeTotals();
    const total = t.estBasic;
    const actTotal = t.actBasic;

    let html = `
    <div style="font-family: Arial, sans-serif; max-width: 850px; margin: 0 auto; color: #333;">
      <div style="background-color: #1a1a2e; padding: 24px; border-radius: 8px 8px 0 0;">
        <h2 style="color: #ffffff; margin: 0;">Bill of Quantities</h2>
        <p style="color: #a0a0b0; margin: 4px 0 0;">
          ${worksheet.name || 'Project BOQ'} ${project ? `- ${project.name}` : ''}
        </p>
      </div>
      
      <div style="padding: 24px; background: #ffffff; border: 1px solid #e5e7eb;">
        ${additionalNote ? `<p style="margin-bottom: 16px; color: #555;">${additionalNote.replace(/\n/g, '<br/>')}</p>` : ''}
        
        <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 16px;">
          <thead>
            <tr style="background-color: #f3f4f6;">
              <th style="padding: 6px 8px; text-align: left; border-bottom: 2px solid #d1d5db;">#</th>
              <th style="padding: 6px 8px; text-align: left; border-bottom: 2px solid #d1d5db;">Description</th>
              <th style="padding: 6px 8px; text-align: center; border-bottom: 2px solid #d1d5db;">Est Qty</th>
              <th style="padding: 6px 8px; text-align: center; border-bottom: 2px solid #d1d5db;">Unit</th>
              <th style="padding: 6px 8px; text-align: right; border-bottom: 2px solid #d1d5db;">Rate</th>
              <th style="padding: 6px 8px; text-align: right; border-bottom: 2px solid #d1d5db; background:#e8f4fd;">Est Amt</th>
              <th style="padding: 6px 8px; text-align: center; border-bottom: 2px solid #d1d5db;">Act Qty</th>
              <th style="padding: 6px 8px; text-align: right; border-bottom: 2px solid #d1d5db; background:#e8fde8;">Act Amt</th>
              <th style="padding: 6px 8px; text-align: right; border-bottom: 2px solid #d1d5db; background:#ecfdf5; color:#047857;">Billable So Far</th>
              <th style="padding: 6px 8px; text-align: right; border-bottom: 2px solid #d1d5db;">Variance</th>
              <th style="padding: 6px 8px; text-align: left; border-bottom: 2px solid #d1d5db;">Remarks</th>
            </tr>
          </thead>
          <tbody>`;

    let serialNo = 0;
    // iter-326: running Billable So Far + count of real items for simple-mean %
    let billableTotal = 0;
    let billableItemCount = 0;
    let billableProgressSum = 0;
    items.forEach((item) => {
      if (item.item_type === 'header') {
        html += `
            <tr style="background-color: #FFF8E1; border-bottom: 2px solid #F59E0B;">
              <td colspan="11" style="padding: 10px 8px; font-weight: bold; color: #92400E; font-size: 13px;">
                ${item.task_title || item.description || ''}
              </td>
            </tr>`;
        return;
      }
      serialNo++;
      const rate = getItemRate(item);
      const estQty = item.quantity || 0;
      const estAmount = estQty * rate;
      const actQty = item.actual_quantity != null ? (parseFloat(item.actual_quantity) || 0) : estQty;
      const actRate = pickActualRate(item, rateView, rate);
      const actAmount = actQty * actRate;
      const variance = actAmount - estAmount;
      const varianceColor = variance > 0 ? '#dc2626' : variance < 0 ? '#16a34a' : '#6b7280';
      // iter-326: Billable So Far = actAmount × task_progress%
      const progressPct = parseFloat(item.task_progress || 0) || 0;
      const billableRaw = actAmount * progressPct / 100;  // accumulate un-rounded
      const billable = Math.round(billableRaw);
      billableTotal += billableRaw;
      billableItemCount += 1;
      billableProgressSum += progressPct;
      html += `
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: 5px 8px;">${serialNo}</td>
              <td style="padding: 5px 8px;">${item.task_title || item.description || ''}</td>
              <td style="padding: 5px 8px; text-align: center;">${estQty}</td>
              <td style="padding: 5px 8px; text-align: center;">${item.unit || '-'}</td>
              <td style="padding: 5px 8px; text-align: right;">₹${rate.toLocaleString()}</td>
              <td style="padding: 5px 8px; text-align: right; background:#f0f7ff;">₹${Math.round(estAmount).toLocaleString()}</td>
              <td style="padding: 5px 8px; text-align: center;">${actQty}</td>
              <td style="padding: 5px 8px; text-align: right; background:#f0fff0;">₹${Math.round(actAmount).toLocaleString()}</td>
              <td style="padding: 5px 8px; text-align: right; background:#ecfdf5; color:#047857; font-weight:600;">${billable > 0 ? '₹' + billable.toLocaleString() : '-'}</td>
              <td style="padding: 5px 8px; text-align: right; color: ${varianceColor};">${variance !== 0 ? (variance > 0 ? '+' : '') + '₹' + Math.round(variance).toLocaleString() : '-'}</td>
              <td style="padding: 5px 8px; color: #6b7280; font-size: 11px;">${item.remarks || item.actual_remarks || ''}</td>
            </tr>`;
    });

    // iter-312o: pull GST + Discount + Overheads + Grand Total directly from the
    // shared helper so the email body matches Edit BOQ / PDF / Excel exactly.
    const estGst = Math.round(t.gst);
    const actGst = Math.round(t.actGst);
    const estDiscountAmt = Math.round(t.discount);
    const actDiscountAmt = Math.round(t.actDiscount);
    const discountType = worksheet.discount_type || 'value';
    const discountPercentage = parseFloat(
      worksheet.discount_type === 'percentage'
        ? (worksheet.discount_value || worksheet.discount_percentage || 0)
        : 0
    ) || 0;
    const freightPct = worksheet.freight_pct || 0;
    const labourPct = worksheet.labour_handling_pct || 0;
    const consultPct = worksheet.consultancy_pct || 0;
    const estFreight = Math.round(t.freight);
    const actFreight = Math.round(t.actFreight);
    const estLabour = Math.round(t.labourHandling);
    const actLabour = Math.round(t.actLabour);
    const estConsult = Math.round(t.consultancy);
    const actConsult = Math.round(t.actConsultancy);
    const estGrand = Math.round(t.estGrand);
    const actGrand = Math.round(t.actGrand);

    // iter-326d: ratio for proportional Billable cascade in footer
    const billableRatio = t.actBasic > 0 ? (billableTotal / t.actBasic) : 0;
    const bsfFor = (actVal) => Math.round((actVal || 0) * billableRatio);
    const milestoneEst = rateView === 'vendor' ? 0 : computeConsultancyMilestone(worksheet.consultancy_stage, worksheet.items || [], t.consultancy, t.actConsultancy).est;
    const billableGrandFinal = Math.round(applyMilestoneToBillableGrand(bsfFor(actGrand), bsfFor(actConsult), milestoneEst));

    const footerRow = (label, est, act, billable, color) => {
      const v = act - est;
      const vc = v > 0 ? '#dc2626' : v < 0 ? '#16a34a' : '#6b7280';
      const bsfCell = billable !== null && billable !== undefined
        ? `<td style="padding: 6px 8px; text-align: right; background:#ecfdf5; color:#047857; font-weight:600; ${color ? 'color:' + color + ';' : ''}">${billable === 0 ? '—' : (billable < 0 ? '-' : '') + '₹' + Math.abs(billable).toLocaleString()}</td>`
        : `<td style="padding: 6px 8px; text-align: right; background:#ecfdf5; color:#9ca3af;">—</td>`;
      return `<tr style="background-color: #f3f4f6;">
        <td colspan="5" style="padding: 6px 8px; text-align: right; ${color ? 'color:' + color + ';' : ''}">${label}</td>
        <td style="padding: 6px 8px; text-align: right; background:#f0f7ff; ${color ? 'color:' + color + ';' : ''}">₹${est.toLocaleString()}</td>
        <td style="padding: 6px 8px;"></td>
        <td style="padding: 6px 8px; text-align: right; background:#f0fff0; ${color ? 'color:' + color + ';' : ''}">₹${act.toLocaleString()}</td>
        <td style="padding: 6px 8px;"></td>
        ${bsfCell}
        <td style="padding: 6px 8px; text-align: right; color: ${vc};">${v !== 0 ? (v > 0 ? '+' : '') + '₹' + v.toLocaleString() : '-'}</td>
        <td style="padding: 6px 8px;"></td></tr>`;
    };

    html += `</tbody><tfoot>`;
    // iter-304b/326: SUMMARY heading row to match Edit BOQ / Public BOQ design language (11 cols)
    html += `
      <tr style="background-color: #1a1a2e; color: #fbbf24;">
        <td colspan="11" style="padding: 8px 12px; text-align: left; font-size: 13px; font-weight: bold; letter-spacing: 1px;">SUMMARY</td>
      </tr>
      <tr style="background-color: #f3f4f6; font-weight: 600;">
        <td colspan="5" style="padding: 6px 8px; text-align: left; color:#374151;">Summary</td>
        <td style="padding: 6px 8px; text-align: right; background:#dbeafe; color:#1d4ed8;">ESTIMATED</td>
        <td style="padding: 6px 8px;"></td>
        <td style="padding: 6px 8px; text-align: right; background:#dcfce7; color:#15803d;">ACTUAL</td>
        <td style="padding: 6px 8px; text-align: right; background:#ecfdf5; color:#047857;">BILLABLE</td>
        <td style="padding: 6px 8px; text-align: right; background:#fef3c7; color:#b45309;">VARIANCE</td>
        <td style="padding: 6px 8px;"></td>
      </tr>`;
    html += footerRow(`Basic Total (${viewLabel}):`, Math.round(total), Math.round(actTotal), Math.round(billableTotal));
    if (estDiscountAmt > 0 || actDiscountAmt > 0) {
      html += footerRow(`Discount${discountType === 'percentage' ? ` (${discountPercentage}%)` : ''}:`, -estDiscountAmt, -actDiscountAmt, -bsfFor(actDiscountAmt), '#dc2626');
    }
    if (freightPct > 0) html += footerRow(`Freight (${freightPct}%):`, estFreight, actFreight, bsfFor(actFreight));
    if (labourPct > 0) html += footerRow(`Labour Handling (${labourPct}%):`, estLabour, actLabour, bsfFor(actLabour));
    if (consultPct > 0) html += footerRow(`Consultancy (${consultPct}%)${milestoneEst > bsfFor(actConsult) ? ' · superseded by milestone' : ''}:`, estConsult, actConsult, bsfFor(actConsult));
    if (milestoneEst > 0) html += footerRow(`Consultancy Milestone (Stage ${worksheet.consultancy_stage})${milestoneEst <= bsfFor(actConsult) ? ' · superseded by consultancy %' : ''}:`, Math.round(milestoneEst), Math.round(milestoneEst), Math.round(milestoneEst), '#047857');
    html += footerRow('GST:', estGst, actGst, bsfFor(actGst));

    // iter-326d: Total Billable So Far row — shows cascade-inclusive grand total
    if (billableTotal > 0) {
      const billableGrandRounded = billableGrandFinal;
      const simplePct = billableItemCount > 0 ? Math.round(billableProgressSum / billableItemCount) : 0;
      const labelTxt = `Total Billable So Far${simplePct > 0 ? ` (${simplePct}% complete · cascade applied)` : ''}:`;
      html += `<tr style="background-color: #ecfdf5;">
        <td colspan="5" style="padding: 8px 8px; text-align: right; color:#065f46; font-weight:700;">${labelTxt}</td>
        <td style="padding: 8px 8px; text-align: right; color:#9ca3af;">—</td>
        <td style="padding: 8px 8px;"></td>
        <td style="padding: 8px 8px; text-align: right; color:#9ca3af;">—</td>
        <td style="padding: 8px 8px;"></td>
        <td style="padding: 8px 8px; text-align: right; color:#047857; font-weight:700; background:#d1fae5; font-size:14px;">₹${billableGrandRounded.toLocaleString()}</td>
        <td style="padding: 8px 8px; text-align: right; color:#9ca3af;">—</td>
        <td style="padding: 8px 8px;"></td></tr>`;
    }

    // iter-312r/326d: Grand Total — Billable column shows cascaded grand total
    const gv = actGrand - estGrand;
    const gvc = gv > 0 ? '#dc2626' : gv < 0 ? '#16a34a' : '#fff';
    const grandBsf = billableGrandFinal;
    html += `
            <tr style="background-color: #1a1a2e; color: white; font-weight: bold;">
              <td colspan="5" style="padding: 10px 8px; text-align: right; font-size: 14px;">Grand Total:</td>
              <td style="padding: 10px 8px; text-align: right; font-size: 14px;">₹${estGrand.toLocaleString()}</td>
              <td style="padding: 10px 8px;"></td>
              <td style="padding: 10px 8px; text-align: right; font-size: 14px;">₹${actGrand.toLocaleString()}</td>
              <td style="padding: 10px 8px;"></td>
              <td style="padding: 10px 8px; text-align: right; font-size: 14px; color:#6ee7b7;">${grandBsf > 0 ? '₹' + grandBsf.toLocaleString() : '—'}</td>
              <td style="padding: 10px 8px; text-align: right; color: ${gvc};">${gv !== 0 ? (gv > 0 ? '+' : '') + '₹' + gv.toLocaleString() : '-'}</td>
              <td style="padding: 10px 8px;"></td>
            </tr>`;

    // iter-312p/r/326/330: Actual-only deductions below Grand Total (11-col layout).
    // iter-330: Billable column also mirrors the deduction so Billable Payment
    // Receivable below matches Edit BOQ exactly (SSOT).
    const cpApplied = Math.round(t.clientPaymentsApplied);
    const cnApplied = Math.round(t.creditNotesApplied);
    const dashFooterRow = (label, act) => `<tr style="background-color: #f3f4f6;">
        <td colspan="5" style="padding: 6px 8px; text-align: right;">${label}</td>
        <td style="padding: 6px 8px; text-align: right; background:#f0f7ff; color:#9ca3af;">—</td>
        <td style="padding: 6px 8px;"></td>
        <td style="padding: 6px 8px; text-align: right; background:#f0fff0; color:#dc2626;">−₹${act.toLocaleString()}</td>
        <td style="padding: 6px 8px;"></td>
        <td style="padding: 6px 8px; text-align: right; background:#d1fae5; color:#dc2626; font-weight:700;">−₹${act.toLocaleString()}</td>
        <td style="padding: 6px 8px;"></td>
        <td style="padding: 6px 8px;"></td></tr>`;
    if (cpApplied > 0) html += dashFooterRow('Client Payments Applied (−):', cpApplied);
    if (cnApplied > 0) html += dashFooterRow('Credit Notes Applied (−):', cnApplied);

    // iter-312r/t/326/330: PAYMENT RECEIVABLE — same bold black band as Grand Total.
    // Actual = actGrand − cpApplied − cnApplied. Billable = grandBsf − cpApplied − cnApplied.
    const actReceivable = Math.round(t.actNetReceivable);
    const billableReceivable = Math.max(0, grandBsf - cpApplied - cnApplied);
    html += `
            <tr style="background-color: #1a1a2e; color: white; font-weight: bold;">
              <td colspan="5" style="padding: 10px 8px; text-align: right; font-size: 14px; color: #6ee7b7;">PAYMENT RECEIVABLE:</td>
              <td style="padding: 10px 8px; text-align: right; font-size: 14px; color: #94a3b8;">—</td>
              <td style="padding: 10px 8px;"></td>
              <td style="padding: 10px 8px; text-align: right; font-size: 14px; color: #6ee7b7;">₹${actReceivable.toLocaleString()}</td>
              <td style="padding: 10px 8px;"></td>
              <td style="padding: 10px 8px; text-align: right; font-size: 14px; color: #a7f3d0; background: rgba(110,231,183,0.15);">₹${billableReceivable.toLocaleString()}</td>
              <td style="padding: 10px 8px; text-align: right; color: #94a3b8;">—</td>
              <td style="padding: 10px 8px;"></td>
            </tr>
          </tfoot></table>`;

    // Add Terms & Conditions
    if (worksheet.terms_conditions) {
      html += `
        <div style="margin-top: 20px; padding: 16px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px;">
          <h3 style="margin: 0 0 12px; font-size: 14px; color: #1f2937; font-weight: bold;">Terms & Conditions</h3>
          <div style="font-size: 12px; color: #4b5563; line-height: 1.6; white-space: pre-wrap;">${worksheet.terms_conditions}</div>
        </div>`;
    }

    if (boqLink) {
      html += `
        <div style="text-align: center; margin: 20px 0;">
          <a href="${boqLink}" 
             style="display: inline-block; background-color: #2563eb; color: white; padding: 12px 24px; 
                    border-radius: 6px; text-decoration: none; font-weight: 500; font-size: 14px;">
            View Full BOQ Online
          </a>
        </div>`;
    }

    html += `
      </div>
      <div style="background-color: #f9fafb; padding: 16px 24px; border-radius: 0 0 8px 8px; border: 1px solid #e5e7eb; border-top: 0;">
        <p style="margin: 0; color: #6b7280; font-size: 12px;">
          Sent from Bella Casa Interiors | This is a system-generated email
        </p>
      </div>
    </div>`;

    return html;
  };

  const handleSend = async () => {
    if (!toEmail.trim()) {
      toast.error('Please enter a recipient email');
      return;
    }
    
    setSending(true);
    try {
      // iter-304c: append the role-matched public link + a brief "View online" CTA
      // at the bottom of the email body, and ask the backend to attach the PDF for
      // the same role (Client / Vendor / Admin) so the recipient sees prices in
      // the same currency-of-trust the sender chose.
      const tok = worksheet.public_token || worksheet.client_public_token || worksheet.vendor_public_token;
      const baseUrl = window.location.origin;
      // Pick the public link matching the rate view
      let publicPath = '';
      if (rateView === 'vendor' && (worksheet.vendor_public_token || tok)) {
        publicPath = `/public/boq/${worksheet.vendor_public_token || tok}?role=vendor`;
      } else if (worksheet.public_token || worksheet.client_public_token) {
        publicPath = `/public/boq/${worksheet.public_token || worksheet.client_public_token}`;
      }
      const fullPublicUrl = publicPath ? `${baseUrl}${publicPath}` : '';

      let emailHTML = generateEmailHTML();
      if (fullPublicUrl) {
        const ctaHtml = `
        <div style="margin: 20px 0; padding: 16px; background:#1a1a2e; border-radius:8px; text-align:center;">
          <p style="margin:0 0 10px; color:#fbbf24; font-size:13px; font-weight:bold;">View the live BOQ online</p>
          <a href="${fullPublicUrl}" style="display:inline-block; padding:10px 22px; background:#fbbf24; color:#1a1a2e; text-decoration:none; border-radius:6px; font-weight:bold;">Open ${rateView === 'vendor' ? 'Vendor' : 'Client'} BOQ</a>
          <p style="margin:10px 0 0; color:#a0a0b0; font-size:11px; word-break:break-all;">${fullPublicUrl}</p>
        </div>`;
        // Inject the CTA just above the footer
        emailHTML = emailHTML.replace(
          '<div style="background-color: #f9fafb',
          `${ctaHtml}<div style="background-color: #f9fafb`
        );
      }

      await axios.post(`${API}/send-boq-email`, {
        to_email: toEmail.trim(),
        cc_email: ccEmail.trim() || null,
        subject: subject,
        html_body: emailHTML,
        worksheet_id: worksheetId,
        project_id: projectId,
        // iter-304c: backend will fetch the role-specific PDF and attach it.
        attach_pdf: true,
        pdf_role: rateView,
        public_link: fullPublicUrl,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('BOQ email sent successfully — PDF attached');
      setTimeout(() => window.close(), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen bg-stone-50"><Loader2 className="animate-spin" size={24} /></div>;
  }

  if (!worksheet) {
    return <div className="flex items-center justify-center h-screen bg-stone-50"><p className="text-red-500">BOQ not found</p></div>;
  }

  const items = worksheet.items?.filter(i => !i.is_discount) || [];
  const total = getTotal();

  return (
    <div className="min-h-screen bg-stone-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => window.close()}>
              <ArrowLeft size={16} className="mr-1" /> Close
            </Button>
            <h1 className="font-bold text-lg text-stone-900 flex items-center gap-2">
              <Mail size={20} className="text-green-600" />
              Email BOQ
            </h1>
          </div>
          <Button onClick={handleSend} disabled={sending} className="bg-green-600 hover:bg-green-700 text-white" data-testid="send-boq-email-btn">
            {sending ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
            {sending ? 'Sending...' : 'Send Email'}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
        {/* Left: Email Form */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-stone-200 p-5 space-y-4">
            <h2 className="font-semibold text-stone-800">Compose</h2>
            <div>
              <Label className="text-stone-700">To</Label>
              <Input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="recipient@example.com"
                className="mt-1"
                data-testid="boq-email-to"
              />
            </div>
            <div>
              <Label className="text-stone-700">CC (optional)</Label>
              <Input
                type="email"
                value={ccEmail}
                onChange={(e) => setCcEmail(e.target.value)}
                placeholder="cc@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-stone-700">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1"
                data-testid="boq-email-subject"
              />
            </div>
            <div>
              <Label className="text-stone-700">Rate View</Label>
              <div className="flex gap-2 mt-1 flex-wrap items-center">
                {['vendor', 'admin', 'client'].map(v => (
                  <Button key={v} size="sm" variant={rateView === v ? 'default' : 'outline'}
                    onClick={() => setRateView(v)} className="capitalize">
                    {v}
                  </Button>
                ))}
                {/* iter-304c: one-click copy of the role-matched public link.
                    Saves admins the 3-step open→open public-view→copy-URL flow. */}
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-1 text-stone-700 hover:bg-amber-50 hover:border-amber-300"
                  onClick={async () => {
                    if (!worksheet) { toast.error('Worksheet not loaded yet'); return; }
                    const tok = worksheet.public_token || worksheet.client_public_token || worksheet.vendor_public_token;
                    const baseUrl = window.location.origin;
                    let path = '';
                    if (rateView === 'vendor' && (worksheet.vendor_public_token || tok)) {
                      path = `/public/boq/${worksheet.vendor_public_token || tok}?role=vendor`;
                    } else if (worksheet.public_token || worksheet.client_public_token) {
                      path = `/public/boq/${worksheet.public_token || worksheet.client_public_token}`;
                    }
                    if (!path) { toast.error('Publish this BOQ first to generate a public link'); return; }
                    const url = `${baseUrl}${path}`;
                    try {
                      await navigator.clipboard.writeText(url);
                      toast.success(`${rateView === 'vendor' ? 'Vendor' : 'Client'} public link copied`, { duration: 4000 });
                    } catch {
                      // Fallback for non-https contexts
                      const ta = document.createElement('textarea');
                      ta.value = url; document.body.appendChild(ta); ta.select();
                      document.execCommand('copy'); document.body.removeChild(ta);
                      toast.success('Link copied (legacy)', { duration: 4000 });
                    }
                  }}
                  data-testid="copy-public-link-btn"
                >
                  Copy public link
                </Button>
              </div>
              <p className="text-xs text-stone-400 mt-1">Choose which rate layer to include in the email</p>
            </div>
            <div>
              <Label className="text-stone-700">Additional Note</Label>
              <Textarea
                value={additionalNote}
                onChange={(e) => setAdditionalNote(e.target.value)}
                placeholder="Add a personal note above the BOQ table..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* Right: Preview */}
        <div>
          <div className="bg-white rounded-lg border border-stone-200 p-5">
            <h2 className="font-semibold text-stone-800 mb-3">Preview</h2>
            <div className="border border-stone-200 rounded-lg overflow-hidden">
              <div className="bg-stone-900 p-4">
                <h3 className="text-white font-bold">Bill of Quantities</h3>
                <p className="text-stone-400 text-sm">{worksheet.name} {project ? `- ${project.name}` : ''}</p>
              </div>
              <div className="p-4">
                {additionalNote && <p className="text-sm text-stone-600 mb-3">{additionalNote}</p>}
                <p className="text-xs text-stone-500 mb-2 italic">Email includes: Estimated Qty/Amount, Actual Qty/Amount, Variance, Remarks, and Terms & Conditions</p>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-stone-100">
                      <th className="p-1.5 text-left w-8">#</th>
                      <th className="p-1.5 text-left">Description</th>
                      <th className="p-1.5 text-center w-12">Est Qty</th>
                      <th className="p-1.5 text-right w-16">Rate</th>
                      <th className="p-1.5 text-right w-20 bg-blue-50">Est Amt</th>
                      <th className="p-1.5 text-center w-12">Act Qty</th>
                      <th className="p-1.5 text-right w-20 bg-green-50">Act Amt</th>
                      <th className="p-1.5 text-right w-24 bg-emerald-50 text-emerald-700">Billable So Far</th>
                      <th className="p-1.5 text-right w-16">Variance</th>
                      <th className="p-1.5 text-left w-32">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      if (item.item_type === 'header') {
                        return (
                          <tr key={idx} className="bg-amber-50 border-y-2 border-amber-300">
                            <td colSpan={10} className="p-2 font-bold text-amber-900">{item.task_title || ''}</td>
                          </tr>
                        );
                      }
                      const rate = getItemRate(item);
                      const estAmt = (item.quantity || 0) * rate;
                      const aq = item.actual_quantity != null ? (parseFloat(item.actual_quantity) || 0) : (item.quantity || 0);
                      const ar = pickActualRate(item, rateView, rate);
                      const actAmt = aq * ar;
                      const v = actAmt - estAmt;
                      const remarksText = item.remarks || item.actual_remarks || '';
                      // iter-326: Billable So Far in live preview
                      const progPct = parseFloat(item.task_progress || 0) || 0;
                      const billableAmt = Math.round(actAmt * progPct / 100);
                      return (
                        <tr key={idx} className="border-b border-stone-100">
                          <td className="p-1.5">{idx + 1}</td>
                          <td className="p-1.5">{item.task_title || '-'}</td>
                          <td className="p-1.5 text-center">{item.quantity || 0}</td>
                          <td className="p-1.5 text-right">₹{rate.toLocaleString()}</td>
                          <td className="p-1.5 text-right font-medium bg-blue-50/50">₹{Math.round(estAmt).toLocaleString()}</td>
                          <td className="p-1.5 text-center">{aq}</td>
                          <td className="p-1.5 text-right font-medium bg-green-50/50">₹{Math.round(actAmt).toLocaleString()}</td>
                          <td className="p-1.5 text-right font-semibold bg-emerald-50/60 text-emerald-700">{billableAmt > 0 ? '₹' + billableAmt.toLocaleString() : '-'}</td>
                          <td className={`p-1.5 text-right ${v > 0 ? 'text-red-600' : v < 0 ? 'text-green-600' : 'text-stone-400'}`}>{v !== 0 ? (v > 0 ? '+' : '') + '₹' + Math.round(v).toLocaleString() : '-'}</td>
                          <td className="p-1.5 text-stone-500 text-[11px]" title={remarksText}>{remarksText.length > 30 ? remarksText.slice(0, 30) + '…' : remarksText}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {(() => {
                      // iter-312o: preview footer must use the same helper as the
                      // email body so what the user sees is exactly what gets sent.
                      const tt = computeTotals();
                      const eB = Math.round(tt.estBasic);
                      const aB = Math.round(tt.actBasic);
                      const eD = Math.round(tt.discount);
                      const aD = Math.round(tt.actDiscount);
                      const eGstP = Math.round(tt.gst);
                      const aGstP = Math.round(tt.actGst);
                      const eFr = Math.round(tt.freight);
                      const aFr = Math.round(tt.actFreight);
                      const eLb = Math.round(tt.labourHandling);
                      const aLb = Math.round(tt.actLabour);
                      const eCn = Math.round(tt.consultancy);
                      const aCn = Math.round(tt.actConsultancy);
                      const eG = Math.round(tt.estGrand);
                      const aG = Math.round(tt.actGrand);
                      const gV = aG - eG;
                      const dType = worksheet.discount_type || 'value';
                      const dPctLabel = dType === 'percentage'
                        ? (parseFloat(worksheet.discount_value || worksheet.discount_percentage || 0) || 0)
                        : 0;
                      const fP = worksheet.freight_pct || 0;
                      const lP = worksheet.labour_handling_pct || 0;
                      const cP = worksheet.consultancy_pct || 0;
                      // iter-326d: billable ratio for live preview cascade
                      const realItems = (items || []).filter(it => it.item_type !== 'header');
                      const billableRawPv = realItems.reduce((s, it) => {
                        const ratePv = getItemRate(it);
                        const aQPv = it.actual_quantity != null ? (parseFloat(it.actual_quantity) || 0) : (it.quantity || 0);
                        const aRPv = pickActualRate(it, rateView, ratePv);
                        const actAmtPv = aQPv * aRPv;
                        return s + (actAmtPv * (parseFloat(it.task_progress || 0) || 0) / 100);
                      }, 0);
                      const billableTotalPv = Math.round(billableRawPv);
                      const actSubtotalPv = realItems.reduce((s, it) => {
                        const ratePv = getItemRate(it);
                        const aQPv = it.actual_quantity != null ? (parseFloat(it.actual_quantity) || 0) : (it.quantity || 0);
                        const aRPv = pickActualRate(it, rateView, ratePv);
                        return s + (aQPv * aRPv);
                      }, 0);
                      const ratioPv = actSubtotalPv > 0 ? (billableTotalPv / actSubtotalPv) : 0;
                      const bsfPv = (a) => Math.round((a || 0) * ratioPv);
                      const msPv = rateView === 'vendor' ? 0 : Math.round(computeConsultancyMilestone(worksheet.consultancy_stage, worksheet.items || [], tt.consultancy, tt.actConsultancy).est);
                      const bGrandPv = Math.round(applyMilestoneToBillableGrand(bsfPv(aG), bsfPv(aCn), msPv));
                      const simplePctPv = realItems.length > 0
                        ? Math.round(realItems.reduce((s, it) => s + (parseFloat(it.task_progress || 0) || 0), 0) / realItems.length)
                        : 0;
                      const bRow = (label, eVal, aVal, bVal, cls) => {
                        const rv = aVal - eVal;
                        return (
                          <tr key={label} className="bg-stone-50">
                            <td colSpan={4} className={`p-1 text-right text-[10px] ${cls || 'text-stone-600'}`}>{label}</td>
                            <td className={`p-1 text-right text-[10px] bg-blue-50/30 ${cls || ''}`}>₹{eVal.toLocaleString()}</td>
                            <td className="p-1"></td>
                            <td className={`p-1 text-right text-[10px] bg-green-50/30 ${cls || ''}`}>₹{aVal.toLocaleString()}</td>
                            <td className={`p-1 text-right text-[10px] bg-emerald-50/60 font-semibold ${cls ? cls : 'text-emerald-700'}`}>
                              {bVal === 0 ? '—' : (bVal < 0 ? '-' : '') + '₹' + Math.abs(bVal).toLocaleString()}
                            </td>
                            <td className={`p-1 text-right text-[10px] ${rv > 0 ? 'text-red-600' : rv < 0 ? 'text-green-600' : 'text-stone-400'}`}>{rv !== 0 ? (rv > 0 ? '+' : '') + '₹' + rv.toLocaleString() : '-'}</td>
                            <td className="p-1"></td>
                          </tr>
                        );
                      };
                      return (
                        <>
                          {/* iter-304b/326: SUMMARY heading band so the preview matches the actual email (10 cols) */}
                          <tr className="bg-stone-900 text-amber-300">
                            <td colSpan={10} className="p-1.5 text-left text-[10px] font-bold tracking-wider">SUMMARY</td>
                          </tr>
                          {bRow('Basic Total:', eB, aB, billableTotalPv)}
                          {(eD > 0 || aD > 0) && bRow(`Discount${dType === 'percentage' ? ` (${dPctLabel}%)` : ''}:`, -eD, -aD, -bsfPv(aD), 'text-red-600')}
                          {fP > 0 && bRow(`Freight (${fP}%):`, eFr, aFr, bsfPv(aFr))}
                          {lP > 0 && bRow(`Labour (${lP}%):`, eLb, aLb, bsfPv(aLb))}
                          {cP > 0 && bRow(`Consultancy (${cP}%)${msPv > bsfPv(aCn) ? ' · superseded by milestone' : ''}:`, eCn, aCn, bsfPv(aCn))}
                          {msPv > 0 && bRow(`Consultancy Milestone (Stage ${worksheet.consultancy_stage})${msPv <= bsfPv(aCn) ? ' · superseded by consultancy %' : ''}:`, msPv, msPv, msPv, 'text-emerald-700')}
                          {bRow('GST:', eGstP, aGstP, bsfPv(aGstP))}
                          {/* iter-326d: Total Billable So Far — shows cascade-inclusive grand total (10 cells) */}
                          {billableTotalPv > 0 && (
                            <tr className="bg-emerald-100 border-y-2 border-emerald-400" data-testid="email-preview-billable-row">
                              <td colSpan={4} className="p-1.5 text-right text-[11px] text-emerald-900 font-bold">
                                Total Billable So Far{simplePctPv > 0 ? ` (${simplePctPv}% complete · cascade applied)` : ''}:
                              </td>
                              <td className="p-1.5 text-right text-[10px] text-stone-300">—</td>
                              <td className="p-1.5"></td>
                              <td className="p-1.5 text-right text-[10px] text-stone-300">—</td>
                              <td className="p-1.5 text-right text-[11px] text-emerald-900 font-bold bg-emerald-200">₹{bGrandPv.toLocaleString()}</td>
                              <td className="p-1.5 text-right text-[10px] text-stone-300">—</td>
                              <td className="p-1.5"></td>
                            </tr>
                          )}
                          {/* iter-312r/326: Grand Total — 10 cells */}
                          <tr className="bg-stone-800 text-white font-bold">
                            <td colSpan={4} className="p-1.5 text-right">
                              <span className="inline-flex items-center gap-1.5">
                                <span
                                  className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40"
                                  title="These totals are computed by the shared computeBoqTotals() helper used by Edit BOQ, PDF and Excel — so the recipient will see exactly the same numbers."
                                  data-testid="email-totals-parity-chip"
                                >🔒 Same totals as Edit BOQ</span>
                                Grand Total:
                              </span>
                            </td>
                            <td className="p-1.5 text-right">₹{eG.toLocaleString()}</td>
                            <td className="p-1.5"></td>
                            <td className="p-1.5 text-right">₹{aG.toLocaleString()}</td>
                            <td className="p-1.5 text-right text-emerald-300">₹{bGrandPv.toLocaleString()}</td>
                            <td className={`p-1.5 text-right ${gV > 0 ? 'text-red-300' : gV < 0 ? 'text-green-300' : ''}`}>{gV !== 0 ? (gV > 0 ? '+' : '') + '₹' + gV.toLocaleString() : '-'}</td>
                            <td className="p-1.5"></td>
                          </tr>
                          {Math.round(tt.clientPaymentsApplied) > 0 && (
                            <tr key="cp" className="bg-stone-50">
                              <td colSpan={4} className="p-1 text-right text-[10px] text-stone-600">Client Payments Applied (−):</td>
                              <td className="p-1 text-right text-[10px] bg-blue-50/30 text-stone-300">—</td>
                              <td className="p-1"></td>
                              <td className="p-1 text-right text-[10px] bg-green-50/30 text-red-600">−₹{Math.round(tt.clientPaymentsApplied).toLocaleString()}</td>
                              <td className="p-1 text-right text-[10px] bg-emerald-100 text-red-600 font-semibold" data-testid="email-cp-billable">−₹{Math.round(tt.clientPaymentsApplied).toLocaleString()}</td>
                              <td className="p-1 text-right text-[10px] text-stone-400">-</td>
                              <td className="p-1"></td>
                            </tr>
                          )}
                          {Math.round(tt.creditNotesApplied) > 0 && (
                            <tr key="cn" className="bg-stone-50">
                              <td colSpan={4} className="p-1 text-right text-[10px] text-stone-600">Credit Notes Applied (−):</td>
                              <td className="p-1 text-right text-[10px] bg-blue-50/30 text-stone-300">—</td>
                              <td className="p-1"></td>
                              <td className="p-1 text-right text-[10px] bg-green-50/30 text-red-600">−₹{Math.round(tt.creditNotesApplied).toLocaleString()}</td>
                              <td className="p-1 text-right text-[10px] bg-emerald-100 text-red-600 font-semibold" data-testid="email-cn-billable">−₹{Math.round(tt.creditNotesApplied).toLocaleString()}</td>
                              <td className="p-1 text-right text-[10px] text-stone-400">-</td>
                              <td className="p-1"></td>
                            </tr>
                          )}
                          {(Math.round(tt.clientPaymentsApplied) > 0 || Math.round(tt.creditNotesApplied) > 0) && (() => {
                            const aR = Math.round(tt.actNetReceivable);
                            const bR = Math.max(0, bGrandPv - Math.round(tt.clientPaymentsApplied) - Math.round(tt.creditNotesApplied));
                            return (
                              <tr key="pr" className="bg-stone-800 text-white font-bold" data-testid="email-payment-receivable-row">
                                <td colSpan={4} className="p-1.5 text-right text-emerald-300">PAYMENT RECEIVABLE:</td>
                                <td className="p-1.5 text-right text-stone-500">—</td>
                                <td className="p-1.5"></td>
                                <td className="p-1.5 text-right text-emerald-300">₹{aR.toLocaleString()}</td>
                                <td className="p-1.5 text-right text-emerald-200" data-testid="email-billable-payment-receivable">₹{bR.toLocaleString()}</td>
                                <td className="p-1.5 text-right text-stone-500">—</td>
                                <td className="p-1.5"></td>
                              </tr>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </tfoot>
                </table>
                {worksheet.terms_conditions && (
                  <div className="mt-3 p-2 bg-stone-50 rounded text-[10px] text-stone-500">
                    <strong>Terms & Conditions included in email</strong>
                  </div>
                )}
                {(worksheet.public_link || worksheet.temp_link) && (
                  <div className="mt-3 text-center">
                    <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded">
                      <ExternalLink size={12} /> View Full BOQ Online (link included)
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
